import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useDebouncedValue } from "../../../hooks/useDebouncedValue";
import { adminApi } from "../../../lib/admin";
import type { PropertyPredicateDto } from "../../../types/gigTypes";
import { apiErrorToMessage } from "../../../utils/ApiUtils";
import { endOfLocalDay, parseLocalDateInput, startOfLocalDay, toIsoTimestamp } from "../../../utils/DateUtils";
import AdminReviewsTable from "./AdminReviewsTable";

const DEFAULT_PAGE_SIZE = 10;

const AdminReviewsPanel = () => {
  const [search, setSearch] = useState("");
  const [reviewContext, setReviewContext] = useState("");
  const [createdFromDate, setCreatedFromDate] = useState("");
  const [createdToDate, setCreatedToDate] = useState("");
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [page, setPage] = useState(0);
  const debouncedSearch = useDebouncedValue(search, 300);

  const predicates = useMemo(() => {
    const nextPredicates: PropertyPredicateDto[] = [];
    if (debouncedSearch.trim().length > 0) {
      nextPredicates.push({ propertyName: "search", propertyValue: debouncedSearch.trim(), operation: "CONTAINS" });
    }
    if (reviewContext.trim().length > 0) {
      nextPredicates.push({ propertyName: "reviewContext", propertyValue: reviewContext.trim(), operation: "EQUALS" });
    }

    const createdFrom = parseLocalDateInput(createdFromDate);
    const createdTo = parseLocalDateInput(createdToDate);
    if (createdFrom) {
      nextPredicates.push({ propertyName: "createdAt", propertyValue: toIsoTimestamp(startOfLocalDay(createdFrom)), operation: "TIMESTAMP_FROM" });
    }
    if (createdTo) {
      nextPredicates.push({ propertyName: "createdAt", propertyValue: toIsoTimestamp(endOfLocalDay(createdTo)), operation: "TIMESTAMP_TO" });
    }

    return nextPredicates;
  }, [createdFromDate, createdToDate, debouncedSearch, reviewContext]);

  const reviewsQuery = useQuery({
    queryKey: ["admin-reviews", { page, pageSize, debouncedSearch, reviewContext, createdFromDate, createdToDate }],
    queryFn: () =>
      adminApi.listReviews({
        predicates,
        page: {
          number: page,
          size: pageSize,
          numberOfElements: null,
          hasNext: false,
          hasPrevious: false,
          first: false,
          last: false,
          sortBy: [{ direction: "DESC", propertyName: "createdAt" }],
        },
      }),
    placeholderData: (previousData) => previousData,
  });

  const error = reviewsQuery.error ? apiErrorToMessage(reviewsQuery.error, "Failed to load reviews.") : null;

  return (
    <div>
      {error ? <p className="mb-4 text-sm font-medium text-red-600">{error}</p> : null}
      <AdminReviewsTable
        reviews={reviewsQuery.data?.reviews ?? []}
        isLoading={reviewsQuery.isLoading}
        search={search}
        reviewContext={reviewContext}
        createdFromDate={createdFromDate}
        createdToDate={createdToDate}
        pageSize={pageSize}
        page={page}
        resultsCount={reviewsQuery.data?.page.numberOfElements ?? 0}
        hasNextPage={reviewsQuery.data?.page.hasNext ?? false}
        hasPreviousPage={reviewsQuery.data?.page.hasPrevious ?? false}
        onSearchChange={(value) => {
          setSearch(value);
          setPage(0);
        }}
        onReviewContextChange={(value) => {
          setReviewContext(value);
          setPage(0);
        }}
        onCreatedFromDateChange={(value) => {
          setCreatedFromDate(value);
          setPage(0);
        }}
        onCreatedToDateChange={(value) => {
          setCreatedToDate(value);
          setPage(0);
        }}
        onPageSizeChange={(value) => {
          setPageSize(value);
          setPage(0);
        }}
        onPageChange={setPage}
      />
    </div>
  );
};

export default AdminReviewsPanel;
